import { Action, createReducer, on } from '@ngrx/store';
import * as fromActions from '../actions/sealing-element.actions';
import {
  PipeSchedule,
  ProductFamily,
  SBU,
  SealingElement,
  SealingElementEquipmentType,
  SealingElementEquipmentTypes,
  SealingElementPluggingHead,
  SellableStatus,
  SubSBU
} from 'src/app/models/sealing-element.model';
import { LookupValue } from 'src/app/models/lookup.model';

export const sealingElementFeatureKey = 'sealingElement';

export enum Loaders {
  loadSealingElements,
  loadSealingElementsLookups
}

export enum StringMatcherSymbol {
  Contains = 0,
  StartsWith = 1,
  EndsWith = 2,
  Exact = 4
}

export interface SealingElementFilter {
  region?: string;
  partNumber?: string;
  partNumberMatcher?: StringMatcherSymbol;
  pipeSizeID?: number;
  materialID?: number;
  productFamilyID?: string;
  sbuID?: string;
  subSbuID?: string;
  equipmentTypeID?: number;
  pluggingHeadPartNumber?: string;

  // if user enters the inner diameter,
  // I need to blank wallThickness and use the InnerDiameter they specified
  innerDiameter?: number;

  // if user enters wall thickness,
  // I need to blank inner diameter and calulate the innerDiameter (OD - (wallThickness * 2))
  wallThickness?: number;
}

export interface SealingElementMaintenanceFilter extends SealingElementFilter {
  sellableWHID?: string[];
  sellableEHID?: string[];
  itemName?: string;
  createdBy?: string;
  updatedBy?: string;

  pipeSizeIDs?: number[];
  equipmentTypeIDs?: number[];
  materialIDs?: number[];
}

export interface State {
  loaders: Loaders[];
  sealingElements?: {
    value?: SealingElement[],
    count?: number,
    skip?: number,
    take?: number
  },
  pipeSizes: LookupValue[];
  categories: LookupValue[];
  sealingElementFilter: SealingElementMaintenanceFilter;
  materials: LookupValue[];
  equipmentTypes: SealingElementEquipmentType[];
  productFamilies: ProductFamily[];
  pluggingHeads: SealingElementPluggingHead[];
  sbus: SBU[];
  subSBUs: SubSBU[];
  sellableStatuses: SellableStatus[];
  selectedProduct?: string;
  ansiPipeSizes: PipeSchedule[];

}

export const initialState: State = {
  loaders: [],
  pipeSizes: [],
  categories: [],
  sealingElementFilter: {
    region: 'WH',
    partNumberMatcher: StringMatcherSymbol.StartsWith
  },
  materials: [],
  equipmentTypes: [],
  productFamilies: [],
  sbus: [],
  subSBUs: [],
  sellableStatuses: [],
  ansiPipeSizes: [],
  pluggingHeads: []
};

export const reducer = createReducer(
  initialState,



  // the main load for the resultset
  on(fromActions.loadSealingElements, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElements),
    sealingElements: {
      ...state.sealingElements,
      skip: action.skip,
      take: action.take
    }
  })),
  on(fromActions.loadSealingElementsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElements)
  })),
  on(fromActions.loadSealingElementsSuccess, (state, action) => ({
    ...state,
    sealingElements: {
      ...state.sealingElements,
      count: action.data.count ?? 0,
      value: action.data.value ?? []
    },
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElements)
  })),

  // the selection of a product
  on(fromActions.toggleSelectedProduct, (state, action) => ({
    ...state,
    selectedProduct: state.selectedProduct === action.partNumber ? undefined : action.partNumber
  })),

  // this provides a way to select the next product in the filter results using the down arrow
  on(fromActions.toggleNextSelectedProduct, (state, action) => {
    let newSelection = undefined;
    const curr = (state.sealingElements?.value ?? []).findIndex(i => i.partNumber === state.selectedProduct);
    if ((state.sealingElements?.value ?? []).length > curr + 1) newSelection = (state.sealingElements?.value ?? [])[curr + 1].partNumber;
    return {
      ...state,
      selectedProduct: !!newSelection ? newSelection : state.selectedProduct
    }
  }),

  // this provides a way to select the previous product in the filter results using the up arrow
  on(fromActions.togglePreviousSelectedProduct, (state, action) => {
    let newSelection = undefined;
    const curr = (state.sealingElements?.value ?? []).findIndex(i => i.partNumber === state.selectedProduct);
    if (curr > 0) newSelection = (state.sealingElements?.value ?? [])[curr - 1].partNumber;

    return {
      ...state,
      selectedProduct: !!newSelection ? newSelection : state.selectedProduct
    }
  }),

  // The update to the actual filter
  on(fromActions.updateSealingElementFilter, (state, action) => {
    return ({
      ...state,
      sealingElementFilter: {
        ...state.sealingElementFilter,
        ...action.obj,
        // if the equipment type is not STOPPLE or STOPPLE Train, we set the materialID to undefined
        materialID: [SealingElementEquipmentTypes.STOPPLE, SealingElementEquipmentTypes.STOPPLETrain]
          .includes(action.obj.equipmentTypeID ?? state.sealingElementFilter.equipmentTypeID ?? -1) ? action.obj.materialID : undefined
      }
    })
  }),

  // Lookup values ↓↓↓
  on(fromActions.loadMaterials, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadMaterialsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups),
    materials: action.materials
  })),
  on(fromActions.loadMaterialsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadPipeSizes, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadPipeSizesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups),
    pipeSizes: action.data
  })),
  on(fromActions.loadPipeSizesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadEquipmentTypes, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadEquipmentTypesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups),
    equipmentTypes: action.equipmentTypes
  })),
  on(fromActions.loadEquipmentTypesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadProductFamilies, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadProductFamiliesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadSealingElementsLookups),
    productFamilies: action.families
  })),
  on(fromActions.loadProductFamiliesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadSBUs, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadSBUsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadSealingElementsLookups),
    sbus: action.sbus
  })),
  on(fromActions.loadSBUsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadSubSBUs, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadSubSBUsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups),
    subSBUs: action.subSBUs
  })),
  on(fromActions.loadSubSBUsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),
  ///

  on(fromActions.loadSellables, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadSellablesSuccess, (state, action) => ({
    ...state,
    sellableStatuses: action.sellables,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),
  on(fromActions.loadSellablesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadSealingElementsLookups)
  })),

  // Load ANSI pipes
  on(fromActions.loadAnsiPipeSizesSuccess, (state, action) => ({
    ...state,
    ansiPipeSizes: action.data
  })),

  // load plugging heads
  on(fromActions.loadPluggingHeadsSuccess, (state, action) => ({
    ...state,
    pluggingHeads: action.objs
  })),

  //. removeloader
  on(fromActions.removeLoader, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== action.loader)
  }))

);
